import type { JSX } from 'react';
import { useState } from 'react';
import { Menu } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/features/auth/session';
import { useMyGuardianStudents } from '@/features/guardian/hooks';
import { OrganizationSwitcher } from '@/features/organization/organization-switcher';
import { useIsPersonalSpace } from '@/features/personal-space/hooks';
import { useMyStudentProfile } from '@/features/students/hooks';
import { useHasCapability } from '@/lib/capabilities';
import { useHasPlatformCapability } from '@/lib/platform-capabilities';
import { cn } from '@/lib/utils';

interface NavItem {
  readonly to: string;
  readonly label: string;
}

/** One definition of what a navigation entry looks like, active or not. */
function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return cn(
    'rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary',
    isActive ? 'bg-secondary text-secondary-foreground' : 'text-foreground',
  );
}

/**
 * "Beranda" is the only unconditional entry: it is the one route every
 * authenticated account can always reach, including an account that holds
 * no organization membership at all. "Santri" is capability-gated below
 * rather than living here - it used to be unconditional, which showed a
 * zero-membership account a menu item leading to a screen it has no scope
 * to read anything from.
 */
const BASE_NAV_ITEMS: readonly NavItem[] = [{ to: '/', label: 'Beranda' }];

/**
 * docs/05_INFORMATION_ARCHITECTURE.md lists Memorization among the
 * authenticated entries; it had no route until now. Gated on actually
 * having a Student profile of one's own rather than on a permission: the
 * page is about the reader's own hafalan, and a Teacher or Admin has none
 * (one role per user per organization). Same additive shape as "Anak
 * Saya", which appears only for an account that really has children.
 */
const HAFALAN_ITEM: NavItem = { to: '/hafalan', label: 'Hafalan' };

const STUDENTS_ITEM: NavItem = { to: '/students', label: 'Santri' };

const MY_CHILDREN_ITEM: NavItem = { to: '/guardians/me', label: 'Anak Saya' };
const MURAJAAH_QUEUE_ITEM: NavItem = { to: '/murajaah/queue', label: 'Murajaah' };
const LEARNING_SPACES_ITEM: NavItem = { to: '/learning-spaces', label: 'Ruang Belajar' };
const CLASSES_ITEM: NavItem = { to: '/classes', label: 'Kelas' };
const ORGANIZATION_ITEM: NavItem = { to: '/organization', label: 'Organisasi' };
const PLATFORM_ADMIN_ITEM: NavItem = { to: '/platform', label: 'Platform Admin' };

function NavLinks({
  items,
  onNavigate,
}: {
  readonly items: readonly NavItem[];
  readonly onNavigate?: () => void;
}): JSX.Element {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          onClick={onNavigate}
          className={navLinkClassName}
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** The account entries below the main nav, in both the aside and the Sheet. */
function AccountLinks({ onNavigate }: { readonly onNavigate?: () => void }): JSX.Element {
  return (
    <>
      <NavLink to="/profil" onClick={onNavigate} className={navLinkClassName}>
        Profil
      </NavLink>
      <NavLink to="/account/sessions" onClick={onNavigate} className={navLinkClassName}>
        Sesi &amp; Perangkat
      </NavLink>
    </>
  );
}

/**
 * The authenticated Mahfuz shell (Phase G-0, Job 8/13). "Anak Saya" is
 * shown additively - only when the current account actually has an
 * ACTIVE Guardian relationship - never as a mutually-exclusive "Parent
 * mode": a Teacher or Admin who is also a Guardian sees both their normal
 * navigation and this entry (ADR-025's Addendum, Decision I: role name is
 * irrelevant to Guardian capability). "Platform Admin" (G-9, Job 6) is the
 * same kind of additive entry, gated on a PLATFORM capability rather than
 * an organization one - it links to `/platform`, a route that sits outside
 * this layout's own Active Organization gate, so it renders for a
 * zero-membership Platform Super Admin exactly as it does for anyone else.
 *
 * "Hafalan" is the same additive shape, keyed on having a Student profile
 * rather than a permission. The pondok-only entries below it narrow inside
 * a personal space - see `inPondok`.
 */
export function AppLayout(): JSX.Element {
  const { user, logout } = useAuth();
  const { data: myChildren } = useMyGuardianStudents();
  const canManageMurajaah = useHasCapability('murajaah_sessions:manage');
  const canManageStudents = useHasCapability('students:manage');
  const canViewStudents = useHasCapability('students:read');
  const canViewLearningSpaces = useHasCapability('learning_spaces:read');
  const canViewClasses = useHasCapability('classes:read');
  const canViewOrganization = useHasCapability('organizations:read');
  const canManagePlatformOrganizations = useHasPlatformCapability('organizations:manage');
  const { data: myStudentProfile } = useMyStudentProfile(!canManageStudents);
  const isPersonalSpace = useIsPersonalSpace();
  const [mobileOpen, setMobileOpen] = useState(false);

  const hasOwnHafalan = myStudentProfile !== null && myStudentProfile !== undefined;

  // A personal space is a tenant of one (ADR-031): it has no roster, no
  // learning spaces and no classes, and the Student role that owns it
  // nonetheless holds the read permissions for all three. Gating these on
  // permission alone therefore offered a solo learner three menu entries
  // that could only ever lead to empty screens - and a "Santri" list whose
  // single row was the reader themselves.
  const inPondok = !isPersonalSpace;

  const navItems: readonly NavItem[] = [
    ...BASE_NAV_ITEMS,
    ...(hasOwnHafalan ? [HAFALAN_ITEM] : []),
    ...(canViewStudents && inPondok ? [STUDENTS_ITEM] : []),
    ...(canViewLearningSpaces && inPondok ? [LEARNING_SPACES_ITEM] : []),
    ...(canViewClasses && inPondok ? [CLASSES_ITEM] : []),
    ...(canManageMurajaah ? [MURAJAAH_QUEUE_ITEM] : []),
    ...(myChildren !== undefined && myChildren.length > 0 ? [MY_CHILDREN_ITEM] : []),
    ...(canViewOrganization ? [ORGANIZATION_ITEM] : []),
    ...(canManagePlatformOrganizations ? [PLATFORM_ADMIN_ITEM] : []),
  ];

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-56 shrink-0 flex-col gap-4 border-r border-border p-4 md:flex">
        <span className="text-lg font-semibold text-primary">Mahfuz</span>
        <OrganizationSwitcher />
        <NavLinks items={navItems} />
        <div className="mt-auto flex flex-col gap-2">
          <Separator />
          <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
          <AccountLinks />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void logout();
            }}
          >
            Keluar
          </Button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-border p-4 md:hidden">
          <span className="text-lg font-semibold text-primary">Mahfuz</span>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Buka menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <div className="mt-4 flex flex-col gap-4">
                <OrganizationSwitcher />
                <NavLinks
                  items={navItems}
                  onNavigate={() => {
                    setMobileOpen(false);
                  }}
                />
                <Separator />
                <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                <AccountLinks
                  onNavigate={() => {
                    setMobileOpen(false);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void logout();
                    setMobileOpen(false);
                  }}
                >
                  Keluar
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
